import { AccountsService } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import { CrossFade } from 'base/ui/cross_fade';
import { useDocumentTitle } from 'base/ui/document_title';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import logoRelume from 'base/ui/logo.file.svg';
import { ToastLayer, ToastQueue } from 'base/ui/toast';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { ManageReferral } from 'blocks/manage_account/manage_referral';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { AccountForm } from './account_form';
import { ManagePlan } from './manage_plan';
import { ManageTeam } from './manage_team';
import styles from './page.css';
import { ManageAccountRoute, manageAccountRouteToUrl } from './routes';

export const ManageAccountPages = observer(function _ManageAccountPages({
  route,
  accountStore,
  featureFlagsStore,
  accountsService,
  analytics,
}: {
  route: ManageAccountRoute;
  featureFlagsStore: FeatureFlagsStore;
  accountStore: AccountStore;
  accountsService: AccountsService;
  analytics: AnalyticsService;
}) {
  const [toastQueue] = React.useState(() => new ToastQueue());

  let content;
  let title;
  switch (route.path) {
    case 'manage-account':
      title = 'Manage Account';
      content = (
        <AccountForm accountStore={accountStore} toastQueue={toastQueue} />
      );
      break;
    case 'manage-account/plan':
      title = 'Manage Plan';
      content = (
        <ManagePlan
          accountStore={accountStore}
          accountsService={accountsService}
          analytics={analytics}
          dialog={route.dialog}
          setDialog={(dialog) =>
            window.history.pushState(
              {},
              '',
              manageAccountRouteToUrl({ path: 'manage-account/plan', dialog }),
            )
          }
        />
      );
      break;
    case 'manage-account/team':
      title = 'Your Team';
      content = (
        <ManageTeam
          accountsService={accountsService}
          toastQueue={toastQueue}
          analytics={analytics}
        />
      );
      break;
    case 'manage-account/referral':
      title = 'Referral Program';
      content = (
        <ManageReferral
          analyticsService={analytics}
          accountsService={accountsService}
          toastQueue={toastQueue}
          featureFlagsStore={featureFlagsStore}
        />
      );
      break;
    default:
      throw new UnreachableError(route);
  }

  useDocumentTitle(`${title} - Relume`);

  return (
    <div className={styles.pageContainer}>
      <Rows spacing={0}>
        <Navbar />
        <div className={styles.pageLayout}>
          <SidebarLinks route={route} />
          <CrossFadePanel panelKey={route.path}>{content}</CrossFadePanel>
        </div>
        <ToastLayer queue={toastQueue} />
      </Rows>
    </div>
  );
});

function CrossFadePanel({
  children,
  panelKey,
}: {
  children: React.ReactNode;
  panelKey: string;
}) {
  return (
    <div className={styles.contentWrapper}>
      <CrossFade transitionKey={panelKey}>
        <div className={styles.contentPanel}>{children}</div>
      </CrossFade>
    </div>
  );
}

const SidebarLinks = observer(function _SidebarLinks({
  route,
}: {
  route: ManageAccountRoute;
}) {
  const buttons: { label: string; route: ManageAccountRoute['path'] }[] = [
    { label: 'Account', route: 'manage-account' },
    { label: 'Manage Plan', route: 'manage-account/plan' },
    { label: 'Your Team', route: 'manage-account/team' },
    { label: 'Referral Program', route: 'manage-account/referral' },
  ];
  return (
    <Rows spacing={8}>
      {buttons.map((button) => (
        <Button
          size="large"
          xalign="left"
          key={button.route}
          variant="tertiary"
          background={route.path === button.route ? 'grey2' : 'transparent'}
          subtlyDisabled={route.path === button.route}
          href={manageAccountRouteToUrl({ path: button.route })}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            window.history.pushState(
              {},
              '',
              manageAccountRouteToUrl({ path: button.route }),
            );
          }}
          label={button.label}
        />
      ))}
    </Rows>
  );
});

function Navbar() {
  return (
    <nav className={styles.nav}>
      <Inline spacing={8} yalign="center">
        <a href="/dashboard" className={styles.logoLink}>
          <img
            src={logoRelume}
            className={styles.logo}
            alt="Relume Library"
            role="presentation"
          />
        </a>
        <SpacerGrow />
        <Button
          variant="subtle"
          href="/dashboard"
          label="Go to Dashboard"
          size="large"
        />
      </Inline>
    </nav>
  );
}
